// react
import { useState } from 'react';
import { useDispatch } from 'react-redux';

// internationalization
import { useTranslation } from 'react-i18next';

// tanstack
import { useNavigate, useParams } from '@tanstack/react-router';

// material ui icons
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import DeviceHubRoundedIcon from '@mui/icons-material/DeviceHubRounded';
import RouterRoundedIcon from '@mui/icons-material/RouterRounded';
import SensorsRoundedIcon from '@mui/icons-material/SensorsRounded';

// material ui
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  CardHeader,
  Grid,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

// api
import type { PostMbusDeviceDto } from '@/api/generated/recalc/endpoints/v2/devices';
import { usePostMbusDeviceMutation } from '@/api/recalc/endpoints/devicesApiSlice';

// components
import { showSnackbar } from '@/redux/snackbarSlice';

type DeviceCreationType = 'sensor' | 'gateway' | 'mbus-sensor';
type TagInput = {
  key: string;
  value: string;
};

const M_BUS_SENSOR_TYPES = [
  'KamstrupMultical21',
  'KamstrupFlowIQXX00Series',
  'KamstrupMultical302',
  'KamstrupMultical603',
  'KamstrupMultical403',
  'KamstrupMultical602',
  'DiehlHydrusWaterMeter',
  'DiehlSharkyHeatMeter',
  'CarloGavazziEm24',
  'HydrometerSharkyHeatMeter',
] as const;

const DEVICE_CREATION_TYPES: Array<{
  value: DeviceCreationType;
  icon: React.ReactNode;
  titleKey: string;
  descriptionKey: string;
}> = [
  {
    value: 'sensor',
    icon: <SensorsRoundedIcon fontSize="small" />,
    titleKey: 'devices.deviceTypeSensor',
    descriptionKey: 'devices.deviceTypeSensorDescription',
  },
  {
    value: 'gateway',
    icon: <RouterRoundedIcon fontSize="small" />,
    titleKey: 'devices.deviceTypeGateway',
    descriptionKey: 'devices.deviceTypeGatewayDescription',
  },
  {
    value: 'mbus-sensor',
    icon: <DeviceHubRoundedIcon fontSize="small" />,
    titleKey: 'devices.deviceTypeMbusSensor',
    descriptionKey: 'devices.deviceTypeMbusSensorDescription',
  },
];

const DeviceCreatePage = () => {
  const { t } = useTranslation('units-gateways-sensors');
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { accountId } = useParams({ strict: false });
  const [postMbusDevice, { isLoading: isCreating }] =
    usePostMbusDeviceMutation();

  const [selectedType, setSelectedType] =
    useState<DeviceCreationType>('mbus-sensor');
  const [displayName, setDisplayName] = useState('');
  const [mBusDeviceId, setMBusDeviceId] = useState('');
  const [mBusSensorType, setMBusSensorType] = useState('');
  const [encryptionKey, setEncryptionKey] = useState('');
  const [description, setDescription] = useState('');
  const [tags, setTags] = useState<TagInput[]>([{ key: '', value: '' }]);

  const isMbusSensorSelected = selectedType === 'mbus-sensor';

  const addTag = () => {
    setTags((prev) => [...prev, { key: '', value: '' }]);
  };

  const removeTag = (indexToRemove: number) => {
    setTags((prev) => {
      if (prev.length <= 1) {
        return prev;
      }

      return prev.filter((_, index) => index !== indexToRemove);
    });
  };

  const updateTag = (
    indexToUpdate: number,
    field: keyof TagInput,
    value: string,
  ) => {
    setTags((prev) =>
      prev.map((tag, index) =>
        index === indexToUpdate ? { ...tag, [field]: value } : tag,
      ),
    );
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!isMbusSensorSelected) {
      dispatch(
        showSnackbar({
          message: t('devices.deviceTypeComingSoon'),
          severity: 'info',
        }),
      );
      return;
    }

    if (!accountId) {
      dispatch(
        showSnackbar({
          message: t('devices.accountMissing'),
          severity: 'error',
        }),
      );
      return;
    }

    const normalizedTags = tags.map((tag) => ({
      key: tag.key.trim(),
      value: tag.value.trim(),
    }));
    const validTags = normalizedTags.filter((tag) => tag.key && tag.value);

    if (!displayName.trim() || !mBusDeviceId.trim() || !mBusSensorType) {
      dispatch(
        showSnackbar({
          message: t('devices.mbusSensorValidationRequired'),
          severity: 'error',
        }),
      );
      return;
    }

    const payload: PostMbusDeviceDto = {
      accountId,
      displayName: displayName.trim(),
      mBusDeviceId: mBusDeviceId.trim(),
      mBusSensorType: mBusSensorType as PostMbusDeviceDto['mBusSensorType'],
      tags: validTags,
      ...(description.trim() ? { description: description.trim() } : {}),
      ...(encryptionKey.trim() ? { encryptionKey: encryptionKey.trim() } : {}),
    };

    const result = await postMbusDevice({
      postMbusDeviceDto: payload,
    });

    if (!('error' in result)) {
      dispatch(
        showSnackbar({
          message: t('devices.createdSuccess', { name: payload.displayName }),
          severity: 'success',
        }),
      );

      navigate({
        to: `/${accountId}/devices/${result.data.deviceId}`,
      });
    }
    // Error handled by apiErrorMiddleware (snackbar + App Insights).
  };

  return (
    <Stack spacing={3}>
      <Box>
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          {DEVICE_CREATION_TYPES.map((entry) => {
            const isSelected = selectedType === entry.value;
            const isDisabled = entry.value !== 'mbus-sensor';

            return (
              <Grid
                key={entry.value}
                size={{
                  xs: 12,
                  md: 4
                }}>
                <Card
                  variant={isSelected ? 'outlined' : undefined}
                  sx={{
                    borderColor: isSelected ? 'primary.main' : 'divider',
                    boxShadow: isSelected ? 2 : 0,
                    opacity: isDisabled ? 0.9 : 1,
                  }}
                >
                  <CardActionArea
                    onClick={() => setSelectedType(entry.value)}
                    sx={{ height: '100%', alignItems: 'stretch' }}
                  >
                    <CardHeader
                      avatar={entry.icon}
                      title={t(entry.titleKey)}
                      subheader={t(entry.descriptionKey)}
                      action={
                        isDisabled ? (
                          <Typography variant="caption" color="text.secondary">
                            {t('devices.comingSoon')}
                          </Typography>
                        ) : null
                      }
                    />
                  </CardActionArea>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      </Box>

      {!isMbusSensorSelected ? (
        <Alert severity="info">{t('devices.deviceTypeComingSoon')}</Alert>
      ) : (
        <Card>
          <CardHeader
            title={t('devices.mbusSensorFormTitle')}
            subheader={t('devices.mbusSensorFormDescription')}
          />
          <CardContent>
            <Box component="form" onSubmit={handleSubmit} noValidate>
              <Stack spacing={2}>
                <Grid container spacing={2}>
                  <Grid
                    size={{
                      xs: 12,
                      md: 6
                    }}>
                    <TextField
                      required
                      fullWidth
                      label={t('devices.displayName')}
                      value={displayName}
                      onChange={(event) => setDisplayName(event.target.value)}
                    />
                  </Grid>
                  <Grid
                    size={{
                      xs: 12,
                      md: 6
                    }}>
                    <TextField
                      required
                      fullWidth
                      label={t('devices.mBusDeviceId')}
                      value={mBusDeviceId}
                      onChange={(event) => setMBusDeviceId(event.target.value)}
                    />
                  </Grid>
                  <Grid
                    size={{
                      xs: 12,
                      md: 6
                    }}>
                    <TextField
                      select
                      required
                      fullWidth
                      label={t('devices.mBusSensorType')}
                      value={mBusSensorType}
                      onChange={(event) =>
                        setMBusSensorType(event.target.value)
                      }
                    >
                      {M_BUS_SENSOR_TYPES.map((option) => (
                        <MenuItem key={option} value={option}>
                          {t(`devices.mbusSensorTypes.${option}`)}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid size={12}>
                    <Stack spacing={1}>
                      <Typography variant="overline" color="text.secondary">
                        {t('devices.tags')}
                      </Typography>
                      {tags.map((tag, index) => (
                        <Grid container spacing={2} key={`tag-${index}`}>
                          <Grid
                            size={{
                              xs: 12,
                              md: 5
                            }}>
                            <TextField
                              fullWidth
                              label={t('devices.tagKey')}
                              value={tag.key}
                              onChange={(event) =>
                                updateTag(index, 'key', event.target.value)
                              }
                            />
                          </Grid>
                          <Grid
                            size={{
                              xs: 12,
                              md: 5
                            }}>
                            <TextField
                              fullWidth
                              label={t('devices.tagValue')}
                              value={tag.value}
                              onChange={(event) =>
                                updateTag(index, 'value', event.target.value)
                              }
                            />
                          </Grid>
                          <Grid
                            sx={{ display: 'flex', alignItems: 'center' }}
                            size={{
                              xs: 12,
                              md: 2
                            }}>
                            <Button
                              type="button"
                              color="error"
                              onClick={() => removeTag(index)}
                              disabled={tags.length === 1}
                              fullWidth
                            >
                              {t('devices.removeTag')}
                            </Button>
                          </Grid>
                        </Grid>
                      ))}
                      <Box>
                        <Button type="button" onClick={addTag}>
                          {t('devices.addTag')}
                        </Button>
                      </Box>
                    </Stack>
                  </Grid>
                  <Grid
                    size={{
                      xs: 12,
                      md: 6
                    }}>
                    <TextField
                      fullWidth
                      label={t('devices.encryptionKey')}
                      value={encryptionKey}
                      onChange={(event) => setEncryptionKey(event.target.value)}
                    />
                  </Grid>
                  <Grid size={12}>
                    <TextField
                      fullWidth
                      multiline
                      minRows={3}
                      label={t('devices.description')}
                      value={description}
                      onChange={(event) => setDescription(event.target.value)}
                    />
                  </Grid>
                </Grid>

                <Stack direction="row" justifyContent="flex-end" spacing={1}>
                  <Button
                    variant="contained"
                    type="submit"
                    startIcon={<AddRoundedIcon />}
                    disabled={isCreating}
                  >
                    {isCreating
                      ? t('devices.creating')
                      : t('devices.createMbusSensor')}
                  </Button>
                </Stack>
              </Stack>
            </Box>
          </CardContent>
        </Card>
      )}
    </Stack>
  );
};

export default DeviceCreatePage;
